import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { getPanelTypeForRole } from '../../config/menuConfig';
import api from '../../api/apiClient';
import ChatWindow from './ChatWindow';
import { EduGenieAvatarImg } from './edugenieBrand';
import './edugenie.css';

const THEME_KEY = 'edugenie_theme';
const SESSION_KEY = 'edugenie_session_id';
const PANEL_SESSION_KEY = 'edugenie_panel_type';

const PUBLIC_PATH_PREFIXES = [
  '/',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/2fa',
  '/enquiry',
  '/courses',
  '/faq',
  '/privacy-policy',
  '/terms-of-use'
];

const isPublicRoute = (pathname) => {
  if (pathname === '/') return true;
  return PUBLIC_PATH_PREFIXES.some((prefix) => {
    if (prefix === '/') return false;
    return pathname === prefix || pathname.startsWith(`${prefix}/`);
  });
};

const EduGenieWidget = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [booted, setBooted] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || '');
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [typing, setTyping] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [theme, setTheme] = useState(() => localStorage.getItem(THEME_KEY) || 'light');

  const panelType = useMemo(
    () => (user?.role !== undefined && user?.role !== null ? getPanelTypeForRole(user.role) : null),
    [user?.role]
  );

  const shouldRender = useMemo(() => {
    if (authLoading || !isAuthenticated || !user) return false;
    if (isPublicRoute(location.pathname)) return false;
    return true;
  }, [authLoading, isAuthenticated, user, location.pathname]);

  const ensureSession = useCallback(async (forceNew = false) => {
    const existing = forceNew ? null : localStorage.getItem(SESSION_KEY);
    if (existing) {
      setSessionId(existing);
      return existing;
    }

    const response = await api.edugenie.createSession();
    const newId = response.data?.data?.session_id;
    if (!newId) throw new Error('Failed to create chat session');
    localStorage.setItem(SESSION_KEY, newId);
    setSessionId(newId);
    return newId;
  }, []);

  const bootstrap = useCallback(async ({ resetSession = false } = {}) => {
    setLoading(true);
    setError('');
    try {
      if (resetSession) {
        localStorage.removeItem(SESSION_KEY);
        setSessionId('');
        setMessages([]);
      }

      const [configRes, activeSessionId] = await Promise.all([
        api.edugenie.getConfig(),
        ensureSession(resetSession)
      ]);

      if (!configRes.data?.success) {
        throw new Error(configRes.data?.message || 'Failed to load EduGenie');
      }

      const cfg = configRes.data.data;
      if (!cfg.is_enabled) {
        setConfig(null);
        setError('EduGenie is currently disabled');
        return;
      }

      setConfig(cfg);
      if (cfg.panel_type) {
        localStorage.setItem(PANEL_SESSION_KEY, cfg.panel_type);
      }
      if (cfg.default_theme && !localStorage.getItem(THEME_KEY)) {
        setTheme(cfg.default_theme);
      }

      const historyRes = await api.edugenie.getHistory({
        session_id: activeSessionId,
        limit: cfg.max_history_messages || 50
      });

      if (historyRes.data?.success) {
        const turns = historyRes.data.data?.turns || [];
        setMessages(turns);
      }
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Failed to start EduGenie';
      setError(message);
    } finally {
      setBooted(true);
      setLoading(false);
    }
  }, [ensureSession]);

  useEffect(() => {
    if (!shouldRender || !panelType) return undefined;

    const previousPanel = localStorage.getItem(PANEL_SESSION_KEY);
    const panelChanged = Boolean(previousPanel && previousPanel !== panelType);

    if (!booted && !loading) {
      bootstrap({ resetSession: panelChanged });
      return undefined;
    }

    if (booted && panelChanged && !loading) {
      setOpen(false);
      setBooted(false);
    }

    return undefined;
  }, [shouldRender, panelType, booted, loading, bootstrap]);

  useEffect(() => {
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  const handleOpen = () => {
    setOpen(true);
    setUnreadCount(0);
    if (!booted && !loading) {
      bootstrap();
    }
  };

  const handleClose = () => setOpen(false);
  const handleMinimize = () => setOpen(false);

  const handleToggleTheme = () => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  const appendTurns = (turns = []) => {
    if (!turns.length) return;
    setMessages((prev) => {
      const existingIds = new Set(prev.map((m) => m.id));
      const next = [...prev];
      turns.forEach((turn) => {
        if (!existingIds.has(turn.id)) next.push(turn);
      });
      return next;
    });
  };

  const handleSendMessage = async (rawMessage, intentCode = null) => {
    const message = (rawMessage || '').trim();
    if (!message || sending) return;

    setSending(true);
    setTyping(true);
    setError('');
    setDraft('');

    const tempUserId = `temp-u-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        id: tempUserId,
        role: 'user',
        text: message,
        timestamp: new Date().toISOString()
      }
    ]);

    try {
      const activeSessionId = sessionId || (await ensureSession());
      const response = await api.edugenie.chat({
        message,
        session_id: activeSessionId,
        ...(intentCode ? { intent_code: intentCode } : {})
      });

      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Chat request failed');
      }

      const data = response.data.data || {};
      if (data.session_id && data.session_id !== activeSessionId) {
        localStorage.setItem(SESSION_KEY, data.session_id);
        setSessionId(data.session_id);
      }

      setMessages((prev) => prev.filter((m) => m.id !== tempUserId));
      appendTurns(data.turns || []);

      if (!open) {
        setUnreadCount((count) => count + 1);
      }
    } catch (err) {
      const messageText = err.response?.data?.message || err.message || 'Failed to send message';
      setMessages((prev) => prev.filter((m) => m.id !== tempUserId));
      setMessages((prev) => [
        ...prev,
        {
          id: `err-${Date.now()}`,
          role: 'bot',
          text: `Sorry, I couldn't process that. ${messageText}`,
          timestamp: new Date().toISOString(),
          is_error: true,
          retry_text: message,
          retry_intent_code: intentCode
        }
      ]);
      setError(messageText);
    } finally {
      setTyping(false);
      setSending(false);
    }
  };

  const handleSend = () => handleSendMessage(draft);
  const handleSelectQuick = ({ message, intentCode }) => handleSendMessage(message, intentCode);
  const handleRetry = (message, intentCode) => handleSendMessage(message, intentCode || null);

  const handleFeedback = async (conversationId, rating) => {
    try {
      const response = await api.edugenie.submitFeedback({
        conversation_id: conversationId,
        rating
      });
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Feedback failed');
      }

      setMessages((prev) =>
        prev.map((msg) =>
          msg.conversation_id === conversationId && msg.role === 'bot'
            ? { ...msg, feedback_rating: rating }
            : msg
        )
      );
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Failed to submit feedback');
    }
  };

  const handleClear = async () => {
    try {
      const activeSessionId = sessionId || localStorage.getItem(SESSION_KEY);
      if (activeSessionId) {
        await api.edugenie.clearHistory({ session_id: activeSessionId });
      }
      localStorage.removeItem(SESSION_KEY);
      setSessionId('');
      setMessages([]);
      const newSession = await ensureSession();
      setSessionId(newSession);
      toast.success('Chat cleared');
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Failed to clear chat');
    }
  };

  if (!shouldRender) return null;
  // Disabled by settings with no error → hide launcher
  if (booted && !config && !error && !loading) return null;

  return (
    <div className="edugenie-root" data-theme={theme}>
      {open ? (
        <ChatWindow
          config={config || {
            bot_name: 'EduGenie',
            bot_tagline:
              panelType === 'admin'
                ? 'Platform Admin Assistant'
                : panelType === 'org'
                  ? 'Organization Learning Assistant'
                  : panelType === 'instructor'
                    ? 'Instructor Learning Assistant'
                    : 'Your Personal Learning Assistant',
            greeting_text: 'Connecting to EduGenie…',
            panel_type: panelType,
            quick_actions: [],
            suggested_questions: []
          }}
          theme={theme}
          loading={loading && !booted}
          messages={messages}
          user={user}
          typing={typing}
          error={error}
          draft={draft}
          sending={sending}
          onToggleTheme={handleToggleTheme}
          onClear={handleClear}
          onMinimize={handleMinimize}
          onClose={handleClose}
          onDraftChange={setDraft}
          onSend={handleSend}
          onSelectQuick={handleSelectQuick}
          onFeedback={handleFeedback}
          onRetry={handleRetry}
          onDismissError={() => setError('')}
        />
      ) : null}

      <button
        type="button"
        className="edugenie-launcher"
        onClick={open ? handleClose : handleOpen}
        aria-label={open ? 'Close EduGenie chat' : 'Open EduGenie chat'}
        aria-expanded={open}
        tabIndex={0}
      >
        {open ? (
          <FaTimes size={22} />
        ) : (
          <EduGenieAvatarImg className="edugenie-launcher-img" alt="" />
        )}
        {!open && unreadCount > 0 ? (
          <span className="edugenie-badge" aria-label={`${unreadCount} unread messages`}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        ) : null}
      </button>
    </div>
  );
};

export default EduGenieWidget;
